import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './FAQ.css';

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<string | null>(null);

  const toggleFAQ = (index: string): void => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqs = [
    {
      category: 'Getting Started',
      questions: [
        {
          q: 'How do I get started?',
          a: 'Submit an application through our Contact page. We\'ll review your credentials and get back to you within 2 business days. Once approved, we\'ll send setup instructions for the Aircall app and walk you through the platform.'
        },
        {
          q: 'What are the requirements to join?',
          a: 'You must be a licensed travel advisor or working under a licensed agency. You need a smartphone to receive calls via the Aircall app, and you should have experience selling cruises.'
        },
        {
          q: 'Is there an application fee or setup cost?',
          a: 'No. There are no application fees, setup costs, or monthly fees. You only pay for qualified calls you receive.'
        },
        {
          q: 'How long does approval take?',
          a: 'Most applications are reviewed within 1-2 business days. We manually review each application to ensure quality standards.'
        }
      ]
    },
    {
      category: 'How It Works',
      questions: [
        {
          q: 'How do I receive calls?',
          a: 'You\'ll download the Aircall mobile app. When you toggle your availability to "Available," incoming cruise inquiries are routed to you in real-time on your smartphone.'
        },
        {
          q: 'Can I control when I receive calls?',
          a: 'Yes! Toggle your availability on/off anytime in the app. When you\'re unavailable, you won\'t receive any calls or charges. You have complete control.'
        },
        {
          q: 'What happens if I miss a call?',
          a: 'If you don\'t answer, the call goes to the next available agent. You\'re not charged for missed calls.'
        },
        {
          q: 'How many agents does each caller get routed to?',
          a: 'We route each caller to one available agent at a time. If that agent doesn\'t answer, we try the next one. Once an agent answers and has a conversation, that customer is assigned to them exclusively.'
        },
        {
          q: 'Do I get the same customers repeatedly?',
          a: 'Yes! Once you take a call from a customer, all future calls from that phone number are routed directly to you at no additional charge. This allows you to build relationships and earn repeat business.'
        }
      ]
    },
    {
      category: 'Pricing & Billing',
      questions: [
        {
          q: 'How much does it cost?',
          a: 'Standard calls (from social media) are $20 per call. Premium calls (from email subscribers) are $40 per call. You only pay for calls that meet our billable criteria—no monthly fees or contracts.'
        },
        {
          q: 'What makes a call billable?',
          a: 'A call must be: (1) 90+ seconds long, (2) cruise-related, (3) from a real customer (not spam), (4) the first call from that number, and (5) successfully connected. All criteria must be met.'
        },
        {
          q: 'When do I get billed?',
          a: 'Billing is automatic on the 1st of each month for calls from the previous month. Payment is processed via Stripe using your credit/debit card on file.'
        },
        {
          q: 'Can I set a spending limit?',
          a: 'While we don\'t have a hard spending limit feature, you control your spending by toggling availability. When you\'re unavailable, you don\'t receive calls or charges.'
        },
        {
          q: 'What if I don\'t book the customer?',
          a: 'We charge for qualified calls, not bookings. Lead conversion is your responsibility. If a call met all billable criteria but didn\'t result in a booking, that\'s not grounds for a refund.'
        },
        {
          q: 'How do I dispute a charge?',
          a: 'Email billing@cruisespotterconnect.com within 15 days of your invoice with the call ID and reason. We\'ll review the call recording and respond within 3 business days. See our Refund & Dispute Policy for details.'
        }
      ]
    },
    {
      category: 'Lead Quality',
      questions: [
        {
          q: 'What\'s the quality of leads?',
          a: 'Our leads come from 14M+ engaged social media followers and 10K+ email subscribers. These are cruise enthusiasts who actively follow our content. Agents typically see 10-30% conversion rates.'
        },
        {
          q: 'Are these shared leads?',
          a: 'No! Calls are routed in real-time to one agent. Once you answer, that customer is yours exclusively. We don\'t sell the same lead to multiple agents.'
        },
        {
          q: 'What types of cruises do customers ask about?',
          a: 'Variety: Caribbean, Alaska, Mediterranean, river cruises, luxury cruises, family cruises, etc. Our audience is diverse with different budgets and preferences.'
        },
        {
          q: 'How many calls can I expect per month?',
          a: 'It varies based on your availability and how many agents are in the network. We don\'t guarantee a specific volume. The more you\'re available, the more calls you\'ll receive.'
        }
      ]
    },
    {
      category: 'Technical Questions',
      questions: [
        {
          q: 'What app do I need?',
          a: 'Aircall mobile app (iOS or Android). We\'ll provide download links and setup instructions after approval.'
        },
        {
          q: 'Can I use my existing business phone number?',
          a: 'You\'ll receive calls through the Aircall app on your smartphone. You can use your personal or business phone—the app works on any smartphone.'
        },
        {
          q: 'What if I have technical issues with the app?',
          a: 'Contact our technical support at support@cruisespotterconnect.com. We\'re available Monday-Friday, 9 AM - 6 PM EST.'
        },
        {
          q: 'Are calls recorded?',
          a: 'Yes, all calls are recorded for quality assurance, training, and dispute resolution. Recordings are stored securely for 90 days. Customers are informed at the start of calls that they may be recorded.'
        },
        {
          q: 'Can I access call recordings?',
          a: 'Yes! You can access recordings through your dashboard. This is helpful for follow-ups, notes, and reviewing customer needs.'
        }
      ]
    },
    {
      category: 'Cancelation & Contracts',
      questions: [
        {
          q: 'Is there a contract?',
          a: 'No contracts! We operate month-to-month. You can cancel anytime with no fees.'
        },
        {
          q: 'How do I pause service?',
          a: 'Simply toggle your availability to "Unavailable" in the app. You\'ll stop receiving calls immediately. Toggle back to "Available" whenever you\'re ready to resume.'
        },
        {
          q: 'How do I cancel completely?',
          a: 'Email support@cruisespotterconnect.com or agents@cruisespotterconnect.com to request account cancelation. No fees. You\'ll still be billed for calls received before cancelation.'
        },
        {
          q: 'Can I reactivate after canceling?',
          a: 'Yes! Contact support to reactivate. Your account data is preserved for 90 days after cancelation. After that, you\'ll need to create a new account.'
        }
      ]
    },
    {
      category: 'Business & Legal',
      questions: [
        {
          q: 'Am I an employee or independent contractor?',
          a: 'Independent contractor. You\'re responsible for your own taxes, licenses, and insurance. We\'ll issue a 1099 if you earn $600+ per year.'
        },
        {
          q: 'Do I need insurance?',
          a: 'We recommend having errors & omissions (E&O) insurance as a travel advisor, though it\'s not required to join our platform.'
        },
        {
          q: 'Who handles customer payments for bookings?',
          a: 'You do. Customers pay you directly for their cruise bookings. We never handle booking payments—we only charge you for the leads.'
        },
        {
          q: 'What licenses do I need?',
          a: 'You need to be a licensed travel advisor or work under a licensed travel agency, following your state\'s requirements for selling travel.'
        },
        {
          q: 'Can I use this platform if I work for a host agency?',
          a: 'Yes! Many of our agents work with host agencies. You can use our leads as part of your business under your host agency arrangement.'
        }
      ]
    }
  ];

  return (
    <div className="faq">
      <section className="page-hero">
        <div className="container">
          <h1>Frequently Asked Questions</h1>
          <p className="subtitle">Everything you need to know about our platform</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          {faqs.map((category, catIndex) => (
            <div key={catIndex} className="faq-category">
              <h2>{category.category}</h2>
              <div className="faq-list">
                {category.questions.map((faq, qIndex) => {
                  const globalIndex = `${catIndex}-${qIndex}`;
                  const isOpen = openIndex === globalIndex;
                  return (
                    <div key={qIndex} className={`faq-item ${isOpen ? 'open' : ''}`}>
                      <button
                        className="faq-question"
                        onClick={() => toggleFAQ(globalIndex)}
                      >
                        <span>{faq.q}</span>
                        <span className="faq-icon">{isOpen ? '−' : '+'}</span>
                      </button>
                      {isOpen && (
                        <div className="faq-answer">
                          <p>{faq.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="section gray">
        <div className="container cta-section">
          <h2>Still Have Questions?</h2>
          <p>
            Can't find what you're looking for? Our team is here to help.
          </p>
          <Link to="/contact" className="btn btn-large btn-primary">Contact Us</Link>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
